import { useCallback, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import Logger from '@framework/sentry/Logger';
import { useMessageManager } from '@framework/message';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import type {
  IBondScreenerView,
  IBondScreenerViewsResponse,
  TBondScreenerType,
  TBondScreenerFiltersShape,
} from '@/creditflowresearch/domain/vo/Bondscreener';
import BondScreenerViewsRepository from '@/creditflowresearch/infrastructure/repository/BondScreenerViewsRepository';

interface IUseBondScreenerViewsDataProps<TFilters extends TBondScreenerFiltersShape> {
  type: TBondScreenerType,
  initialViews: IBondScreenerViewsResponse<TFilters>,
}

const useBondScreenerViewsData = <TFilters extends TBondScreenerFiltersShape>(
  { type, initialViews }: IUseBondScreenerViewsDataProps<TFilters>,
) => {
  const { getErrorMessage } = useMessageManager();
  const repository = getContainer().get<BondScreenerViewsRepository>(BondScreenerViewsRepository);

  const [views, setViews] = useState<IBondScreenerView<TFilters>[]>(initialViews.views);
  const [activeViewId, setActiveViewId] = useState<string | null>(initialViews.lastActiveViewId);
  const [appliedViewId, setAppliedViewId] = useState<string | null>(initialViews.lastActiveViewId);

  const commitActiveView = useCallback((viewId: string | null): void => {
    setAppliedViewId(viewId);
  }, []);

  const discardActiveView = useCallback((): void => {
    setActiveViewId(appliedViewId);
  }, [appliedViewId]);

  const updateView = useCallback(async (
    viewId: string,
    columns: string[],
    filters: TFilters,
  ): Promise<void> => {
    try {
      await repository.updateView<TFilters>({ viewId, columns, filters });
      setViews((prev) => prev.map((v) => (v.id === viewId ? { ...v, columns, filters } : v)));
    } catch (error) {
      Logger.error({ message: 'Failed to update bond screener view', error });
      AlertManager.error(getErrorMessage(error));
    }
  }, [repository, getErrorMessage]);

  const createView = useCallback(async (
    name: string,
    columns: string[],
    filters: TFilters,
  ): Promise<IBondScreenerView<TFilters> | null> => {
    try {
      const { viewId } = await repository.createView<TFilters>({ type, name, columns, filters });

      const created: IBondScreenerView<TFilters> = { id: viewId, name, columns, filters };
      setViews((prev) => [...prev, created]);
      return created;
    } catch (error) {
      Logger.error({ message: 'Failed to create bond screener view', error });
      AlertManager.error(getErrorMessage(error));
      return null;
    }
  }, [type, repository, getErrorMessage]);

  const renameView = useCallback(async (viewId: string, name: string): Promise<boolean> => {
    try {
      await repository.renameView({ viewId, name });
      setViews((prev) => prev.map((v) => (v.id === viewId ? { ...v, name } : v)));
      return true;
    } catch (error) {
      Logger.error({ message: 'Failed to rename bond screener view', error });
      AlertManager.error(getErrorMessage(error));
      return false;
    }
  }, [repository, getErrorMessage]);

  const deleteView = useCallback(async (viewId: string): Promise<boolean> => {
    try {
      await repository.deleteView({ viewId });
      setViews((prev) => prev.filter((v) => v.id !== viewId));
      return true;
    } catch (error) {
      Logger.error({ message: 'Failed to delete bond screener view', error });
      AlertManager.error(getErrorMessage(error));
      return false;
    }
  }, [repository, getErrorMessage]);

  const activateView = useCallback(async (viewId: string | null): Promise<void> => {
    try {
      if (viewId === null) {
        await repository.useDefaultView({ type });
      } else {
        await repository.activateView({ viewId });
      }
    } catch (error) {
      Logger.error({ message: 'Failed to activate bond screener view', error });
      AlertManager.error(getErrorMessage(error));
    }
  }, [repository, getErrorMessage, type]);

  return {
    views,
    activeViewId,
    appliedViewId,
    setActiveViewId,
    commitActiveView,
    discardActiveView,
    updateView,
    createView,
    renameView,
    deleteView,
    activateView,
  };
};

export default useBondScreenerViewsData;
